/* Debit / Credit Notes — API integration (see GRN_NOTES_API_SPEC.md §3).
 * Backed by v1/purchase-returns (debit) and v1/sales-returns (credit) on the IMS
 * service (httpSupplier). Envelope-aware. */
import { httpSupplier } from "../../HttpCommonALL";
import supplyService from "../../../Services/supplyService";
import { getCurrentUserName, getResolvedWarehouseCode } from "../../../Services/warehouseContext";

const PR_BASE = "v1/purchase-returns";
const SR_BASE = "v1/sales-returns";
/* Audit header — resolved per call so it reflects the currently signed-in user
   (the backend records X-User as createdBy / cancelledBy). */
const xUser = () => ({ "X-User": getCurrentUserName() });

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* Backend PurchaseReturnResponse -> the UI row shape DebitNote renders. */
const fromPurchaseReturnRow = (n = {}) => ({
  noteId: n.returnId ?? n.id,
  noteType: "PURCHASE_RETURN",
  noteNumber: n.returnNumber ?? "",
  /* returnDate is date-only; the clock time lives on createdAt / postedAt. */
  noteDate: n.returnDate ?? n.createdAt ?? "",
  createdAt: n.createdAt ?? "",
  postedAt: n.postedAt ?? "",
  linkedDocNumber: n.purchaseInvoiceNumber ?? "",
  linkedDocId: n.purchaseInvoiceId ?? null,
  supplierId: n.supplierId ?? null,
  supplierName: n.supplierName ?? "",
  warehouseId: n.warehouseId ?? "",
  warehouseName: n.warehouseName ?? "",
  reason: n.reason ?? "",
  remarks: n.remarks ?? "",
  status: (n.status ?? "DRAFT").toString().toUpperCase(),
  itemCount: n.itemCount ?? (Array.isArray(n.items) ? n.items.length : 0),
  taxableAmount: Number(n.taxableAmount) || 0,
  cgstAmount: Number(n.cgstAmount) || 0,
  sgstAmount: Number(n.sgstAmount) || 0,
  netAmount: Number(n.netAmount) || 0,
  items: Array.isArray(n.items) ? n.items : [],
});

/* Backend SalesReturnResponse -> the UI row shape SalesReturn renders. */
const fromSalesReturnRow = (n = {}) => ({
  noteId: n.returnId ?? n.id,
  noteType: "SALES_RETURN",
  noteNumber: n.returnNumber ?? "",
  /* returnDate is date-only; the clock time lives on createdAt / postedAt. */
  noteDate: n.returnDate ?? n.createdAt ?? "",
  createdAt: n.createdAt ?? "",
  postedAt: n.postedAt ?? "",
  linkedDocNumber: n.salesInvoiceNumber ?? "",
  linkedDocId: n.salesInvoiceId ?? null,
  customerName: n.customerName ?? "",
  customerPhone: n.customerPhone ?? "",
  /* Store credit needs the sale to name a patient or a phone. */
  patientId: n.patientId ?? "",
  warehouseId: n.warehouseId ?? "",
  warehouseName: n.warehouseName ?? "",
  reason: n.reason ?? "",
  remarks: n.remarks ?? "",
  status: (n.status ?? "DRAFT").toString().toUpperCase(),
  itemCount: n.itemCount ?? (Array.isArray(n.items) ? n.items.length : 0),
  taxableAmount: Number(n.taxableAmount) || 0,
  cgstAmount: Number(n.cgstAmount) || 0,
  sgstAmount: Number(n.sgstAmount) || 0,
  netAmount: Number(n.netAmount) || 0,
  items: Array.isArray(n.items) ? n.items : [],
  /* One row per payout, detail view only (the list omits them). refundMode is
     the mode actually paid out — ORIGINAL_PAYMENT_MODE is resolved on posting. */
  refunds: Array.isArray(n.refunds) ? n.refunds : [],
  refundedAmount: Number(n.refundedAmount) || 0,
  /* False = no payout recorded against the note (legacy / no refund plan). */
  refundSettled: n.refundSettled ?? null,
});

const notesService = {
  /* Purchase Returns (Debit Notes) — backed by v1/purchase-returns.
     See GRN_NOTES_API_SPEC.md §3 / purchase-return-controller. */
  purchaseReturns: {
    /* GET v1/purchase-returns -> paged list. Filters: supplierId, warehouseId,
       purchaseInvoiceId, status, fromDate, toDate, search + page/size/sort. */
    list(params = {}) {
      /* Scope to the store in scope app-wide (the topbar switcher — see
         warehouseContext). The endpoint filters on warehouseId only when the param
         is present, so omitting it means "every warehouse", not "mine".
         `|| undefined` keeps an unresolved code out of the query string rather
         than sending warehouseId= and having it read as no filter anyway. */
      const query = {
        warehouseId: getResolvedWarehouseCode() || undefined,
        page: 0,
        size: 20,
        sort: "returnDate,desc",
        ...params,
      };
      return httpSupplier.get(PR_BASE, { params: query, headers: xUser() }).then((r) => {
        const data = unwrap(r);
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        return {
          content: content.map(fromPurchaseReturnRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? query.page,
          size: data?.size ?? query.size,
        };
      });
    },

    get(id) {
      return httpSupplier.get(`${PR_BASE}/${id}`, { headers: xUser() }).then((r) => fromPurchaseReturnRow(unwrap(r)));
    },

    /* POST v1/purchase-returns. `payload` is a PurchaseReturnRequest — items carry
       only inventoryId/quantity/purchasePrice/discountAmount/gstPercentage; the
       server computes the tax breakdown. */
    create(payload) {
      return httpSupplier.post(PR_BASE, payload, { headers: xUser() }).then((r) => unwrap(r));
    },

    /* PATCH v1/purchase-returns/{id} -> edit a DRAFT (full PurchaseReturnRequest). */
    update(id, payload) {
      return httpSupplier.patch(`${PR_BASE}/${id}`, payload, { headers: xUser() }).then((r) => unwrap(r));
    },

    /* POST v1/purchase-returns/{id}/post -> commits stock (RETURN_OUT) + ledger. */
    post(id) {
      return httpSupplier.post(`${PR_BASE}/${id}/post`, null, { headers: xUser() }).then((r) => unwrap(r));
    },

    /* POST v1/purchase-returns/{id}/cancel -> CancelPurchaseReturnRequest { reason }. */
    cancel(id, reason) {
      return httpSupplier.post(`${PR_BASE}/${id}/cancel`, { reason }, { headers: xUser() }).then((r) => unwrap(r));
    },
  },

  /* Sales Returns (Credit Notes) — backed by v1/sales-returns. */
  salesReturns: {
    /* GET v1/sales-returns -> paged list. Filters: warehouseId, salesInvoiceId,
       status, fromDate, toDate, search + page/size/sort. */
    list(params = {}) {
      /* Scope to the store in scope app-wide (the topbar switcher — see
         warehouseContext). The endpoint filters on warehouseId only when the param
         is present, so omitting it means "every warehouse", not "mine".
         `|| undefined` keeps an unresolved code out of the query string rather
         than sending warehouseId= and having it read as no filter anyway. */
      const query = {
        warehouseId: getResolvedWarehouseCode() || undefined,
        page: 0,
        size: 20,
        sort: "returnDate,desc",
        ...params,
      };
      return httpSupplier.get(SR_BASE, { params: query, headers: xUser() }).then((r) => {
        const data = unwrap(r);
        const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
        return {
          content: content.map(fromSalesReturnRow),
          totalPages: data?.totalPages ?? (content.length ? 1 : 0),
          totalElements: data?.totalElements ?? content.length,
          page: data?.number ?? query.page,
          size: data?.size ?? query.size,
        };
      });
    },

    get(id) {
      return httpSupplier.get(`${SR_BASE}/${id}`, { headers: xUser() }).then((r) => fromSalesReturnRow(unwrap(r)));
    },

    /* POST v1/sales-returns. `payload` is a SalesReturnRequest — items carry only
       inventoryId/quantity/sellingPrice/discountAmount/gstPercentage; the server
       computes the tax breakdown. `refunds` [{ refundMode, amount?, transactionId? }]
       must add up to the (rupee-rounded) netAmount; a single line may omit amount. */
    create(payload) {
      return httpSupplier.post(SR_BASE, payload, { headers: xUser() }).then((r) => unwrap(r));
    },

    /* PATCH v1/sales-returns/{id} -> edit a DRAFT (full SalesReturnRequest).
       The server clears the refund plan and re-applies `refunds`, so an update
       without them leaves the note unsettled. */
    update(id, payload) {
      return httpSupplier.patch(`${SR_BASE}/${id}`, payload, { headers: xUser() }).then((r) => unwrap(r));
    },

    /* POST v1/sales-returns/{id}/post -> commits stock (RETURN_IN) + ledger. */
    post(id) {
      return httpSupplier.post(`${SR_BASE}/${id}/post`, null, { headers: xUser() }).then((r) => unwrap(r));
    },

    /* POST v1/sales-returns/{id}/cancel -> CancelSalesReturnRequest { reason }. */
    cancel(id, reason) {
      return httpSupplier.post(`${SR_BASE}/${id}/cancel`, { reason }, { headers: xUser() }).then((r) => unwrap(r));
    },
  },

  /* Find the purchases that contain a given medicine, by code or by name. Used
     when the operator has the product in hand but not the invoice number; the
     caller picks one of these and passes it to loadPurchaseLines. Returns the
     same purchase rows the invoice lookup produces. */
  async lookupPurchasesByMedicine({ medicineCode = "", medicineName = "", warehouseId } = {}) {
    const code = String(medicineCode || "").trim();
    const name = String(medicineName || "").trim();
    if (!code && !name) throw new Error("Enter a medicine name or medicine ID.");
    const rows = await supplyService.getGrnsByMedicine({
      medicineCode: code,
      medicineName: name,
      ...(warehouseId ? { warehouseId } : {}),
    });
    if (!rows || !rows.length) {
      throw new Error(`No purchase found containing “${code || name}”.`);
    }
    return rows;
  },

  /* Search by medicine and come back with the individual returnable LINES, not
     just the purchases that contain them — the operator is looking for a
     medicine, so that is what the results should list.

     by-medicine only returns purchase rows, so each match is opened to resolve
     its lots and then filtered down to the medicine searched for. That is one
     round trip per purchase, hence the `maxPurchases` cap; `scanned` vs `total`
     lets the caller say when the tail was not opened.

     Returns { groups: [{ purchase, lines }], scanned, total }. */
  async lookupReturnableLinesByMedicine({
    medicineCode = "",
    medicineName = "",
    warehouseId,
    maxPurchases = 15,
  } = {}) {
    const purchases = await this.lookupPurchasesByMedicine({ medicineCode, medicineName, warehouseId });
    const scanned = purchases.slice(0, maxPurchases);
    const loaded = await Promise.all(
      scanned.map((p) => this.loadPurchaseLines(p, { warehouseId }).catch(() => null))
    );

    const code = String(medicineCode || "").trim().toLowerCase();
    const name = String(medicineName || "").trim().toLowerCase();
    /* Code matches exactly; a name matches as a substring, mirroring how the
       operator typed a partial name into the search box. */
    const isMatch = (l) =>
      code
        ? String(l.medicineCode || "").toLowerCase() === code
        : String(l.medicineName || "").toLowerCase().includes(name);

    const groups = [];
    loaded.forEach((res, i) => {
      if (!res) return;
      const lines = (res.lines || []).filter(isMatch);
      if (lines.length) groups.push({ purchase: res.purchase || scanned[i], lines });
    });
    return { groups, scanned: scanned.length, total: purchases.length };
  },

  /* Fetch the original purchase by invoice number and resolve each line to its
     current inventory lot (inventoryId + available qty) so the return can be
     entered against real stock. Returns { purchase, lines }. */
  async lookupPurchaseByInvoice(invoiceNumber, { warehouseId } = {}) {
    const inv = String(invoiceNumber || "").trim();
    if (!inv) throw new Error("Enter a purchase invoice number.");
    const { content: grns = [] } = await supplyService.getGrns({ search: inv, size: 10 });
    const purchase =
      grns.find((g) => String(g.supplierInv).toLowerCase() === inv.toLowerCase()) || grns[0];
    if (!purchase) throw new Error(`No purchase found for invoice “${inv}”.`);
    return this.loadPurchaseLines(purchase, { warehouseId });
  },

  /* Resolve a purchase's items to what is still returnable. Shared by both entry
     points (invoice lookup and medicine search) so a return entered either way is
     built against the same figures. Returns { purchase, lines }.

     One call to GET v2/purchases/{id}, which already answers the question this
     screen asks. It replaces a 1 + N fan-out (v1 detail, then a live-batch fetch
     per distinct medicine) that computed the cap from on-hand stock alone — so a
     line already returned once could be returned again up to whatever remained on
     the shelf. `returnableQuantity` nets that off server-side.

     `availableQty` keeps its name because the whole screen enforces the cap
     through it; what changed is the number behind it. */
  async loadPurchaseLines(purchase, { warehouseId } = {}) {
    const detail = await supplyService.getPurchaseReturnView(purchase.id);
    const items = Array.isArray(detail?.items) ? detail.items : [];

    const lines = items.map((it) => {
      /* v2 groups by batch and lists the lots behind the group. Prefer the lot
         with stock to return from; fall back to the first so a fully-consumed
         batch still shows a row (disabled, with the backend's reason). */
      const lots = Array.isArray(it.lots) ? it.lots : [];
      const lot = lots.find((l) => Number(l.returnableQuantity) > 0) || lots[0] || null;
      const returnable = Number(it.returnableQuantity) || 0;
      return {
        inventoryId: lot?.inventoryId ?? null,
        medicineCode: it.medicineCode,
        medicineName: it.medicineName,
        hsnCode: it.hsnCode || "",
        batchNo: it.batchNo,
        expiryDate: it.expiryDate,
        receivedQty: Number(it.quantityReceived) || 0,
        /* The cap: net of what has already been returned, not just what is on the
           shelf. */
        availableQty: returnable,
        purchasePrice: Number(it.purchasePrice) || 0,
        discountPercentage: Number(it.discountPercentage) || 0,
        gstPercentage: Number(it.gstPercentage) || 0,
        /* Carried for the UI to explain a zero cap instead of leaving the
           operator guessing why a line cannot be returned. */
        returnable: it.returnable !== false && returnable > 0,
        notReturnableReason: it.notReturnableReason || "",
        alreadyReturnedQty: Number(it.alreadyReturnedQuantity) || 0,
        quantityOnHand: Number(it.quantityOnHand) || 0,
        returnQty: "",
      };
    });

    /* Header-level eligibility, so a caller can say "nothing on this invoice is
       returnable" without walking the lines. */
    const enriched = {
      ...purchase,
      returnEligible: detail?.returnEligible !== false,
      totalReturnableQuantity: Number(detail?.totalReturnableQuantity) || 0,
      totalReturnedQuantity: Number(detail?.totalReturnedQuantity) || 0,
    };
    return { purchase: enriched, lines };
  },

  /* Fetch the return ledger for a sales invoice — the authoritative view of what
     was sold vs already returned, and how much is STILL eligible to return.
     GET v1/sales-returns/ledger?invoiceNumber=… . GST rate is resolved from the
     medicine master (the ledger doesn't carry it); the per-line discount % is
     backed out of soldAmount vs sellingPrice×soldQuantity. Returns { sale, lines }
     where each line's `eligibleQty` is the cap the UI must enforce. */
  async lookupSaleByInvoice(invoiceNumber, { warehouseId } = {}) {
    const inv = String(invoiceNumber || "").trim();
    if (!inv) throw new Error("Enter a sales invoice number.");
    const [ledger, meds] = await Promise.all([
      httpSupplier
        .get(`${SR_BASE}/ledger`, { params: { invoiceNumber: inv }, headers: xUser() })
        .then((r) => unwrap(r)),
      supplyService.getMedicines().catch(() => []),
    ]);
    const d = ledger || {};
    if (!d.salesInvoiceId) throw new Error(`No sales invoice found for “${inv}”.`);
    const gstFor = (code) => {
      const m = (meds || []).find((x) => String(x.sku) === String(code) || String(x.id) === String(code));
      return m ? Number(m.gst) || 0 : 0;
    };
    const sale = {
      id: d.salesInvoiceId,
      number: d.salesInvoiceNumber,
      invoiceDate: d.invoiceDate,
      status: d.invoiceStatus,
      customerName: d.customerName,
      customerPhone: d.customerPhone,
      patientId: d.patientId,
      warehouseId: d.warehouseId,
      warehouseName: d.warehouseName,
      soldQuantity: d.soldQuantity,
      returnedQuantity: d.returnedQuantity,
      eligibleReturnQuantity: d.eligibleReturnQuantity,
      fullyReturned: d.fullyReturned,
    };
    const lines = (d.lines || []).map((it) => {
      /* `sellingPrice` is PER STRIP, but a UNIT line counts `soldQuantity` in
         loose units. Bring the rate down to the billed unit or every derived
         figure is unitsPerStrip times too big — a 5-unit line off a ₹21.41 strip
         of 10 produced a ₹107.05 gross (vs the real ₹10.71), a nonsense 91.5%
         discount, and would have refunded ₹107.05 for a ₹9.10 sale. */
      const uom = String(it.unitOfMeasure || "STRIP").toUpperCase();
      const unitsPerStrip = Number(it.unitsPerStrip) || null;
      const perBilled = uom === "UNIT" && unitsPerStrip > 0 ? unitsPerStrip : 1;
      const rate = (Number(it.sellingPrice) || 0) / perBilled;
      const soldQty = Number(it.soldQuantity) || 0;
      const gross = round2(rate * soldQty);
      const soldAmount = Number(it.soldAmount) || 0;
      /* Derive the original line discount % from the ledger's net sold amount. */
      const discountPercentage = gross > 0 ? round2((1 - soldAmount / gross) * 100) : 0;
      return {
        inventoryId: it.inventoryId ?? null,
        medicineCode: it.medicineCode,
        medicineName: it.medicineName,
        hsnCode: it.hsnCode,
        batchNo: it.batchNo,
        expiryDate: it.expiryDate,
        soldQty,
        returnedQty: Number(it.returnedQuantity) || 0,
        /* The cap the UI enforces — what's still returnable on this line. */
        eligibleQty: Number(it.eligibleReturnQuantity) || 0,
        eligibleAmount: Number(it.eligibleReturnAmount) || 0,
        fullyReturned: !!it.fullyReturned,
        /* Carried so the UI can label quantities "5 Strips" / "5 Units". */
        uom,
        unitsPerStrip,
        /* The untouched per-STRIP price. `rate` above is per billed unit for the
           UI's math; the API's `sellingPrice` field is per strip (its response
           echoes 120.00 with unitsPerStrip 10 for a lineTotal of 12.00), so the
           payload must send this one, not `rate`. */
        sellingPriceStrip: Number(it.sellingPrice) || 0,
        rate,
        discountPercentage,
        gstPercentage: gstFor(it.medicineCode),
        returnQty: "",
      };
    });
    return { sale, lines };
  },

  /* Per-return-line money math (mirrors GRN §1, scaled to the return qty).
     `rate` is the purchase rate (purchase return) or selling price (sales return). */
  calcLine(line) {
    const qty = Number(line.returnQty) || 0;
    const rate = Number(line.rate ?? line.purchasePrice ?? line.sellingPrice) || 0;
    const gross = round2(qty * rate);
    const discountAmount = round2((gross * (Number(line.discountPercentage) || 0)) / 100);
    const taxableAmount = Math.max(0, round2(gross - discountAmount));
    const gstPercentage = Number(line.gstPercentage) || 0;
    const cgstAmount = round2((taxableAmount * gstPercentage) / 200);
    const sgstAmount = round2((taxableAmount * gstPercentage) / 200);
    const lineTotal = round2(taxableAmount + cgstAmount + sgstAmount);
    return { qty, gross, discountAmount, taxableAmount, gstPercentage, cgstAmount, sgstAmount, lineTotal };
  },
};

export default notesService;
export { round2 as notesRound2 };
